
import React, { useEffect, useState } from 'react';
import { Box, Loader2, Zap, Home, Mountain } from 'lucide-react';
import { chatApi } from '../lib/api';
import AiTaskProgressOverlay from './AiTaskProgressOverlay';
import XuanYuanTopBar from './XuanYuanTopBar';

interface DecoderProps {
  onBack: () => void;
  intro?: string;
}

interface EnvironmentItem {
  name: string;
  features: string;
  reasoning: string;
}

interface DecodeResult {
  inner: EnvironmentItem[];
  outer: EnvironmentItem[];
}

const GODS = ["", "值符", "螣蛇", "太阴", "六合", "白虎", "玄武", "九地", "九天"];
const STARS = ["", "天蓬", "天任", "天冲", "天辅", "天英", "天芮", "天柱", "天心", "天禽"];
const DOORS = ["", "休门", "生门", "伤门", "杜门", "景门", "死门", "惊门", "开门"];
const DECODER_LOADING_STEPS = ['读取宫位符号', '检索象意知识', '推演室内外映射', '整理最终输出'];
const DECODER_LOADING_DURATION_MS = 35000;
const LOADING_PROGRESS_TICK_MS = 100;
const LOADING_PROGRESS_MAX_PERCENT = 96;

function extractJsonText(raw: string): string {
  const text = String(raw || '')
    .replace(/^\uFEFF/, '')
    .replace(/^json\s*/i, '')
    .trim();
  if (!text) return text;

  const codeBlock = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (codeBlock?.[1]) return codeBlock[1].trim();

  const startObj = text.indexOf('{');
  const startArr = text.indexOf('[');
  let start = -1;
  if (startObj >= 0 && startArr >= 0) start = Math.min(startObj, startArr);
  else start = Math.max(startObj, startArr);
  if (start < 0) return text;

  const open = text[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === open) depth += 1;
    if (ch === close) {
      depth -= 1;
      if (depth === 0) {
        return text.slice(start, i + 1).trim();
      }
    }
  }
  return text.slice(start).trim();
}

const Decoder: React.FC<DecoderProps> = ({ onBack, intro }) => {
  const [formData, setFormData] = useState({ god: '', star: '', door: '', heaven: '', earth: '', leadStem: '', userNote: '' });
  const [attributes, setAttributes] = useState({
    isVoid: false,
    doorPo: false,
    heavenXing: false,  // 天盘干击刑
    heavenMu: false,    // 天盘干入墓
    earthXing: false,   // 地盘干击刑
    earthMu: false,     // 地盘干入墓
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgressPercent, setLoadingProgressPercent] = useState(0);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [result, setResult] = useState<DecodeResult | null>(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const asOn = (key: string): boolean => ['1', 'true', 'yes'].includes((q.get(key) || '').toLowerCase());
    const nextGod = q.get('god') || '';
    const nextStar = q.get('star') || '';
    const nextDoor = q.get('door') || '';
    const nextHeaven = q.get('heaven') || q.get('heavenStem') || '';
    const nextEarth = q.get('earth') || q.get('earthStem') || '';
    const nextLead = q.get('leadStem') || '';

    setFormData((prev) => ({
      ...prev,
      god: GODS.includes(nextGod) ? nextGod : prev.god,
      star: STARS.includes(nextStar) ? nextStar : prev.star,
      door: DOORS.includes(nextDoor) ? nextDoor : prev.door,
      heaven: nextHeaven || prev.heaven,
      earth: nextEarth || prev.earth,
      leadStem: nextLead || prev.leadStem,
    }));
    setAttributes((prev) => ({
      ...prev,
      isVoid: asOn('void') || prev.isVoid,
      doorPo: asOn('doorPo') || prev.doorPo,
      heavenXing: asOn('heavenXing') || prev.heavenXing,
      heavenMu: asOn('heavenMu') || prev.heavenMu,
      earthXing: asOn('earthXing') || prev.earthXing,
      earthMu: asOn('earthMu') || prev.earthMu,
    }));
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setLoadingProgressPercent(0);
      setLoadingStepIndex(0);
      return;
    }
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const rawPercent = (elapsed / DECODER_LOADING_DURATION_MS) * 100;
      const nextPercent = Math.max(0, Math.min(LOADING_PROGRESS_MAX_PERCENT, rawPercent));
      const ratio = nextPercent / 100;
      const nextStep = Math.min(
        DECODER_LOADING_STEPS.length - 1,
        Math.floor(ratio * DECODER_LOADING_STEPS.length),
      );
      setLoadingProgressPercent(nextPercent);
      setLoadingStepIndex(nextStep);
    };
    tick();
    const timer = window.setInterval(tick, LOADING_PROGRESS_TICK_MS);
    return () => window.clearInterval(timer);
  }, [isLoading]);

  /** 从可能被 markdown 包裹的文本中提取 JSON 并解析 */
  function extractAndParseJson(raw: string): { inner: EnvironmentItem[]; outer: EnvironmentItem[] } {
    let parsed: unknown;
    try {
      parsed = JSON.parse(extractJsonText(raw));
    } catch {
      throw new Error('返回格式异常，请重试');
    }
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(extractJsonText(parsed));
      } catch {
        throw new Error('返回格式异常，请重试');
      }
    }
    if (!parsed || typeof parsed !== 'object') throw new Error('返回格式异常，请重试');
    const obj = parsed as Record<string, unknown>;

    const toItems = (arr: unknown): EnvironmentItem[] => {
      if (!Array.isArray(arr)) return [];
      return arr.map((item: unknown) => {
        const o = item as Record<string, unknown>;
        return {
          name: typeof o?.name === 'string' ? o.name : String(o?.name ?? o?.名称 ?? ''),
          features: typeof o?.features === 'string' ? o.features : String(o?.features ?? o?.特征 ?? ''),
          reasoning: typeof o?.reasoning === 'string' ? o.reasoning : String(o?.reasoning ?? o?.推理 ?? o?.说明 ?? ''),
        };
      });
    };

    const inner = toItems(obj.inner ?? obj.室内 ?? obj.innerEnvironment ?? []);
    const outer = toItems(obj.outer ?? obj.室外 ?? obj.outerEnvironment ?? []);

    if (inner.length === 0 && outer.length === 0) throw new Error('返回格式异常，请重试');
    return { inner, outer };
  }

  const handleDecode = async () => {
    setIsLoading(true);
    setResult(null);
    try {
      const prompt = [
        '【输入】阴盘奇门符号',
        `符号：${JSON.stringify(formData)}`,
        `状态：${JSON.stringify(attributes)}`,
      ].join('\n');
      const { text } = await chatApi({
        messages: [{ role: 'user', content: prompt }],
        jsonMode: true,
        feature: 'decoder',
        knowledgeQuery: `${formData.god} ${formData.star} ${formData.door} ${formData.heaven} ${formData.earth}`.trim(),
      });
      if (text) {
        const { inner, outer } = extractAndParseJson(text);
        setResult({ inner, outer });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '解码失败。';
      alert(msg === '会员已过期' ? '会员已过期，请重新激活。' : msg);
    } finally {
      setIsLoading(false);
    }
  };

  const AttributeBadge = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button onClick={onClick} className={`px-2 py-1 text-[10px] rounded border ${active ? 'bg-red-50 border-red-200 text-red-600 font-bold' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>{label}</button>
  );
  const ToggleSwitch = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative h-6 w-11 rounded-full transition-colors ${checked ? 'bg-fuchsia-600' : 'bg-slate-300'}`}
      aria-label="切换空亡"
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  return (
    <div className="h-full min-h-0 bg-slate-50 flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·象意解读器"
        onBack={onBack}
        subtitle={intro}
        variant="light"
      />
      <div className="flex-1 overflow-y-auto p-4 lg:p-8">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-800 mb-4">锁定宫位符号</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">八神</label>
                <select
                  value={formData.god}
                  onChange={(e) => setFormData({ ...formData, god: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                >
                  {GODS.map((g) => <option key={g} value={g}>{g || '选择八神'}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">九星</label>
                <select
                  value={formData.star}
                  onChange={(e) => setFormData({ ...formData, star: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                >
                  {STARS.map((s) => <option key={s} value={s}>{s || '选择九星'}</option>)}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">八门</label>
                  <AttributeBadge label="门迫" active={attributes.doorPo} onClick={() => setAttributes({ ...attributes, doorPo: !attributes.doorPo })} />
                </div>
                <select
                  value={formData.door}
                  onChange={(e) => setFormData({ ...formData, door: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                >
                  {DOORS.map((d) => <option key={d} value={d}>{d || '选择八门'}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">天盘干</label>
                <div className="mt-1 flex items-center gap-1">
                  <input
                    value={formData.heaven}
                    onChange={(e) => setFormData({ ...formData, heaven: e.target.value })}
                    className="w-16 max-w-[4rem] p-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                    placeholder="天盘干"
                  />
                  <span className="flex gap-1">
                    <AttributeBadge label="击刑" active={attributes.heavenXing} onClick={() => setAttributes({ ...attributes, heavenXing: !attributes.heavenXing })} />
                    <AttributeBadge label="入墓" active={attributes.heavenMu} onClick={() => setAttributes({ ...attributes, heavenMu: !attributes.heavenMu })} />
                  </span>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">地盘干</label>
                <div className="mt-1 flex items-center gap-1">
                  <input
                    value={formData.earth}
                    onChange={(e) => setFormData({ ...formData, earth: e.target.value })}
                    className="w-16 max-w-[4rem] p-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                    placeholder="地盘干"
                  />
                  <span className="flex gap-1">
                    <AttributeBadge label="击刑" active={attributes.earthXing} onClick={() => setAttributes({ ...attributes, earthXing: !attributes.earthXing })} />
                    <AttributeBadge label="入墓" active={attributes.earthMu} onClick={() => setAttributes({ ...attributes, earthMu: !attributes.earthMu })} />
                  </span>
                </div>
              </div>

              <div className="col-span-2 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400 uppercase">引干</span>
                  <input
                    placeholder="引干"
                    value={formData.leadStem}
                    onChange={(e) => setFormData({ ...formData, leadStem: e.target.value })}
                    className="w-16 p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400 uppercase">空亡</span>
                  <ToggleSwitch checked={attributes.isVoid} onChange={() => setAttributes({ ...attributes, isVoid: !attributes.isVoid })} />
                </div>
              </div>
            </div>

            <button
              onClick={handleDecode}
              disabled={isLoading}
              className="mt-6 w-full py-4 bg-fuchsia-700 hover:bg-fuchsia-800 text-white rounded-xl font-bold flex justify-center items-center gap-2 shadow-md"
            >
              {isLoading ? <Loader2 className="animate-spin w-5 h-5" /> : <Zap className="w-5 h-5" />} {isLoading ? '分析中' : '解码物品'}
            </button>
          </div>
          <div className="lg:col-span-3 space-y-6">
            {result ? (
              <>
                <div className="bg-white p-6 rounded-2xl border shadow-sm"><h3 className="font-bold text-slate-800 mb-4 border-b pb-2 flex gap-2"><Home className="text-fuchsia-500"/>室内物品</h3>{result.inner.map((item, i) => <div key={i} className="mb-4"><strong>{String(item?.name ?? '')}</strong><p className="text-sm text-slate-600">{String(item?.reasoning ?? '')}</p></div>)}</div>
                <div className="bg-white p-6 rounded-2xl border shadow-sm"><h3 className="font-bold text-slate-800 mb-4 border-b pb-2 flex gap-2"><Mountain className="text-indigo-500"/>外部景象</h3>{result.outer.map((item, i) => <div key={i} className="mb-4"><strong>{String(item?.name ?? '')}</strong><p className="text-sm text-slate-600">{String(item?.reasoning ?? '')}</p></div>)}</div>
              </>
            ) : <div className="h-48 border-2 border-dashed rounded-2xl flex items-center justify-center text-slate-300">解码结果显示区</div>}
          </div>
        </div>
      </div>
      {isLoading && (
        <AiTaskProgressOverlay
          title="AI 象意解读中……"
          etaLabel="25~35秒"
          progressPercent={loadingProgressPercent}
          steps={DECODER_LOADING_STEPS}
          stepIndex={loadingStepIndex}
        />
      )}
    </div>
  );
};
export default Decoder;
